import {
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryExecutionProgressResponse,
  CrossClientQueryPhase,
  CrossClientQueryResult,
  CrossClientQueryResultStatus,
} from '../../models/CrossClient'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { createApi } from '@reduxjs/toolkit/query/react'

const _AI_MANAGEMENT_BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

interface GetCrossClientQueryExecutionResultsRequest extends BaseRequest {
  executionId: string
  updatedAfter?: number | null
}

interface GetAllCrossClientQueryExecutionsRequest extends BaseRequest {
  phase?: CrossClientQueryPhase
}

interface GetCrossClientQueryExecutionsRequest extends BasePageRequest {
  phase?: CrossClientQueryPhase
}

interface GetCrossClientQueryExecutionRequest extends BaseRequest {
  executionId: string
}

export const crossClientApi = createApi({
  reducerPath: 'crossClientApi',
  baseQuery: baseQueryWithTokenRefresh(_AI_MANAGEMENT_BASE_URL),
  endpoints: (builder) => ({
    getAllCrossClientQueryExecutions: builder.query<BaseResponse<CrossClientQueryExecution[]>, GetAllCrossClientQueryExecutionsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryExecution>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions`,
            params: { ...(request.phase && { phase: request.phase }) },
          },
          fetchWithBQ,
        )
      },
    }),
    getCrossClientQueryExecutions: builder.query<BasePageResponse<CrossClientQueryExecution[]>, GetCrossClientQueryExecutionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.phase && { phase: request.phase }),
          },
        }
      },
    }),
    getCrossClientQueryExecution: builder.query<CrossClientQueryExecution, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}`,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecution }) => response.data,
    }),
    getAllCrossClientQueryExecutionResults: builder.query<BaseResponse<CrossClientQueryResult[]>, GetCrossClientQueryExecutionResultsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryResult>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/results`,
            params: { ...(request.updatedAfter && { updatedAfter: request.updatedAfter }) },
          },
          fetchWithBQ,
        )
      },
    }),
    getCrossClientQueryExecutionProgress: builder.query<CrossClientQueryExecutionProgress | null, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/progress`,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecutionProgressResponse[] }) => {
        if (!response.data.length) {
          return null
        }
        return {
          executionId: response.data[0].executionId,
          phase: response.data[0].phase,
          lastUpdatedAt: response.data[0].lastUpdatedAt,
          statuses: response.data.reduce(
            (record, response) => {
              record[response.status] = response.resultIds.length
              return record
            },
            {} as Record<CrossClientQueryResultStatus, number>,
          ),
        }
      },
    }),
  }),
})

export const {
  useLazyGetCrossClientQueryExecutionsQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useLazyGetAllCrossClientQueryExecutionsQuery,
  useLazyGetAllCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
} = crossClientApi
